/**
 * QQI Programme Design Studio - React entry point.
 * Initializes the application and renders the root React component.
 * @module index
 */

import React from "react";
import { createRoot } from "react-dom/client";

import { initTheme } from "./components/shared/theme";
import { AppShell } from "./components/shell/AppShell";
import { announceDevBuild } from "./dev/build-canary";
import { createAppRouter } from "./router/routes";
import { computeTenantBasename } from "./server/tenant-basename";
import { initBrowserTelemetry, resolveTelemetryBrowserConfig } from "./telemetry/tracer-provider";
import type { Programme } from "./types";

import "@fontsource-variable/inter";
import "@phosphor-icons/web/regular";
import "@phosphor-icons/web/bold";
// 129KB woff2, the smallest of the six weights. Carried for state: a filled
// glyph says "this control is doing something" in a way a colour change alone
// cannot, and a weight whose font is missing renders an empty box.
import "@phosphor-icons/web/fill";
import "./style.css";

/**
 * Initialize and render the application.
 */
async function init(): Promise<void> {
  // Stripped from a production build: `__IMPERSONATION__` is a literal `false`
  // there, so the branch and the module it imports are both removed. See
  // src/dev/build-canary.ts.
  if (__IMPERSONATION__) {
    announceDevBuild();
  }

  initTheme();

  // Browser telemetry: on unless VITE_OTEL_ENABLED is exactly "false", the
  // same shape as VITE_ENTRA_MULTI_TENANT. See src/telemetry/tracer-provider.ts.
  const telemetryConfig = resolveTelemetryBrowserConfig(
    import.meta.env as unknown as Record<string, string | undefined>,
    __APP_VERSION__,
  );
  if (telemetryConfig) {
    initBrowserTelemetry(telemetryConfig);
  }

  // Get root element
  const rootElement = document.getElementById("app");
  if (!rootElement) {
    console.error("Root element #app not found");
    return;
  }

  // Create React root and render
  const root = createRoot(rootElement);

  // E2E identity injection: when VITE_E2E_IDENTITY is set, bypass real Entra
  // and authenticate as the named user automatically. Loaded only in dev builds.
  let developmentCreateApp: Parameters<typeof AppShell>[0]["createApp"];
  let developmentAuth: Parameters<typeof AppShell>[0]["developmentAuth"];
  const e2eIdentity = import.meta.env.VITE_E2E_IDENTITY?.trim();
  if (typeof __IMPERSONATION__ !== "undefined" && __IMPERSONATION__ && e2eIdentity) {
    const devAuthMod = await import("./auth/development-auth");
    developmentCreateApp = devAuthMod.createDevelopmentApp(e2eIdentity);
    developmentAuth = {
      identities: devAuthMod.DEVELOPMENT_IDENTITIES,
      selectedIdentityId: devAuthMod.selectedDevelopmentIdentity(e2eIdentity).id,
      signIn: devAuthMod.signInDevelopmentIdentity,
    };
  }

  // The router is created once, up front, against the browser's actual
  // location. Its basename is a tenant slug in path-addressed mode (derived
  // from the same tenantSlugFromPath used everywhere else a tenant is
  // resolved from the path) or "/" in host-addressed mode.
  const router = createAppRouter(computeTenantBasename(window.location.pathname));

  root.render(
    <React.StrictMode>
      <AppShell
        createApp={developmentCreateApp}
        developmentAuth={developmentAuth}
        router={router}
        createRouter={createAppRouter}
      />
    </React.StrictMode>,
  );

  // Store reference for legacy compatibility
  (window as Window & { __pds_state?: object }).__pds_state = {
    get programme() {
      // Access via store module
      return (window as Window & { __pds_store?: { programme?: Programme } }).__pds_store
        ?.programme as Programme;
    },
  };
}

// Start the app when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    init();
  });
} else {
  init();
}
